import {
  action,
  autorun,
  computed,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  reaction,
  makeObservable,
} from "mobx";
import { fromPromise, type IPromiseBasedObservable, PENDING } from "mobx-utils";

import Api from "nvent-web/services/Api";
import { type DetailedProject } from "nvent-web/types/DetailedProject";
import { type NewProjectFormValues } from "nvent-web/types/NewProjectFormValues";
import { type Project } from "nvent-web/types/Project";
import { createInitialFromPromise } from "nvent-web/utils/createInitialFromPromise";

import {
  ProjectSearch,
  type ProjectSearchInitialFilters,
} from "./ProjectsSearch";
import { UserStore } from "./User";
import type { Navigate } from "../utils/withRouter";

export class ProjectLocalState {
  isExpanded = false;

  areDetailsExpanded = true;

  constructor(projectId: number) {
    makeObservable(this, {
      isExpanded: observable,
      areDetailsExpanded: observable,
      toggleExpanded: action.bound,
      toggleDetailsExpanded: action.bound,
    });

    const key = `projects.${projectId}.state`;
    const stored = localStorage.getItem(key);

    if (stored) {
      Object.assign(this, JSON.parse(stored));
    }

    autorun(() => {
      localStorage.setItem(
        key,
        JSON.stringify({
          isExpanded: this.isExpanded,
          areDetailsExpanded: this.areDetailsExpanded,
        })
      );
    });
  }

  toggleExpanded(isExpanded = !this.isExpanded) {
    this.isExpanded = isExpanded;
  }

  toggleDetailsExpanded(areExpanded = !this.areDetailsExpanded) {
    this.areDetailsExpanded = areExpanded;
  }
}

export class ProjectsStore {
  projectsPromise = createInitialFromPromise<Project[]>([]);
  newProjectPromise?: IPromiseBasedObservable<Project>;
  projectDetailsPromise?: IPromiseBasedObservable<DetailedProject>;

  private searches: ProjectSearch[] = [];
  private localStateMap = observable.map<number, ProjectLocalState>();

  constructor(
    user: UserStore,
    private api: Api,
    private navigate: Navigate
  ) {
    makeObservable(this, {
      projectsPromise: observable,
      newProjectPromise: observable,
      projectDetailsPromise: observable,
      areProjectsLoading: computed,
      projects: computed,
      loadProjects: action.bound,
      isProjectLoading: computed,
      createProject: action.bound,
      getLocalState: action.bound,
      updateProject: action.bound,
      areProjectDetailsLoading: computed,
      projectDetails: computed,
      getProject: action.bound,
      removeProject: action.bound,
      copyProject: action.bound,
      archiveProject: action.bound,
      setProjectsPromise: action.bound,
      setNewProjectPromise: action.bound,
      setProjectDetailsPromise: action.bound,
    });

    reaction(
      () => user.isReady && user.id,
      (id) => {
        if (id) {
          this.loadProjects();
        }
      },
      { fireImmediately: true }
    );
  }

  setProjectDetailsPromise(value: typeof this.projectDetailsPromise) {
    this.projectDetailsPromise = value;
  }

  setNewProjectPromise(value: typeof this.newProjectPromise) {
    this.newProjectPromise = value;
  }

  setProjectsPromise(value: typeof this.projectsPromise) {
    this.projectsPromise = value;
  }

  get areProjectsLoading(): boolean {
    return this.projectsPromise.state === PENDING;
  }

  get projects(): Project[] {
    return (
      this.projectsPromise.case({
        fulfilled: (projects) => projects,
      }) || []
    );
  }

  async loadProjects() {
    this.setProjectsPromise(
      fromPromise(this.api.team.projects.getAll().then(({ data }) => data))
    );

    this.searches.forEach((search) => search.loadResults());
  }

  get isProjectLoading(): boolean {
    return Boolean(
      this.newProjectPromise && this.newProjectPromise.state === PENDING
    );
  }

  async createProject(values: NewProjectFormValues) {
    this.setNewProjectPromise(
      fromPromise(this.api.projects.create(values).then(({ data }) => data))
    );

    const newProject = await this.newProjectPromise;

    if (!newProject) {
      throw new Error("Failed to create project");
    }

    this.setProjectsPromise(
      fromPromise(Promise.resolve([newProject, ...this.projects]))
    );

    this.navigate(`/projects/${newProject.id}`);
  }

  getLocalState(id: number): ProjectLocalState {
    let state = this.localStateMap.get(id);

    if (!state) {
      state = new ProjectLocalState(id);
      this.localStateMap.set(id, state);
    }

    return state;
  }

  async updateProject(values: NewProjectFormValues, id: number) {
    this.setNewProjectPromise(
      fromPromise(
        this.api.projects.updateOne(values, id).then(({ data }) => data)
      )
    );

    await this.newProjectPromise;
    await this.loadProjects();
  }

  get areProjectDetailsLoading(): boolean {
    return Boolean(
      this.projectDetailsPromise && this.projectDetailsPromise.state === PENDING
    );
  }

  get projectDetails(): DetailedProject | null {
    return this.projectDetailsPromise
      ? this.projectDetailsPromise.case({
          fulfilled: (project) => project,
        })
      : null;
  }

  async getProject(id: number) {
    this.setProjectDetailsPromise(
      fromPromise(this.api.projects.getOne(id).then(({ data }) => data))
    );
  }

  async removeProject(id: number) {
    await this.api.projects.remove(id);
    await this.loadProjects();
  }

  async copyProject(id: number) {
    await this.api.projects.copy(id);
    await this.loadProjects();
  }

  async archiveProject(id: number) {
    await this.api.projects.archive(id);
    await this.loadProjects();
  }

  downloadReport(id: number) {
    return this.api.projects.downloadCommissionReportV2(id);
  }

  downloadBillOfMaterials(id: number) {
    return this.api.projects.downloadBillOfMaterialsV2(id);
  }

  buildSearch(initialFilters: ProjectSearchInitialFilters): ProjectSearch {
    const search = new ProjectSearch(this.api, initialFilters);

    onBecomeObserved(search, "results", () => {
      this.searches.push(search);
    });
    onBecomeUnobserved(search, "results", () => {
      this.searches.splice(this.searches.indexOf(search), 1);
    });

    return search;
  }
}
